/**
 * EVERY BRAND LOGO, IN ONE PLACE.
 *
 * The owner's words: "logos of each card - it's important for positioning".
 * A page of two-letter initials reads as a to-do list; the same page with
 * Shopify's bag and Meta's infinity reads as a product that already plugs into
 * the stack the client runs. So every connector card, Destinations card and
 * forwarding grid draws its mark from this one map, through BrandMark.
 *
 * The files live in public/logos and each one's origin is written down in
 * public/logos/SOURCES.md: Simple Icons (CC0, the official path in the brand's
 * hex) where it has the brand as a single-colour mark, the brand's own site
 * everywhere else - Google's own multicolour product icons rather than Simple
 * Icons' one-colour flattening of them. Nothing here is redrawn or approximated;
 * a brand whose mark cannot be found gets no entry, and the test fails until
 * it is either found or deliberately listed as generic.
 *
 * Per logo:
 *   file  path under public/ (the app is served from /app, see LOGO_BASE)
 *   fit   'mark' (default): a glyph, centred on a white tile with padding
 *         'bleed': the brand's own app-icon square, drawn edge to edge so its
 *         colour IS the tile rather than a square inside a square
 *   bg    tile colour for a mark that needs its brand ground: yellow for
 *         Mailchimp and Snapchat, whose marks vanish on white, and the
 *         indigo Knowlarity sets its white mark on
 *
 * Pure data - no JSX - so jest can load it.
 */

// Root-absolute under /app, the way commerce-source's Bitespeed mark is: the
// SPA is served from /app/ and a bare "/logos/x.svg" 301s to the shell.
export const LOGO_BASE = '/app/';

export const LOGOS = {
  // Simple Icons, CC0: the brand's official path in its own hex.
  shopify: { file: 'logos/shopify.svg' },
  woocommerce: { file: 'logos/woocommerce.svg' },
  bigcommerce: { file: 'logos/bigcommerce.svg' },
  stripe: { file: 'logos/stripe.svg' },
  razorpay: { file: 'logos/razorpay.svg' },
  hubspot: { file: 'logos/hubspot.svg' },
  calendly: { file: 'logos/calendly.svg' },
  mailchimp: { file: 'logos/mailchimp.svg', bg: '#FFE01B' },
  kit: { file: 'logos/kit.svg' },
  whatsapp: { file: 'logos/whatsapp.svg' },
  meta: { file: 'logos/meta.svg' },
  tiktok: { file: 'logos/tiktok.svg' },
  snapchat: { file: 'logos/snapchat.svg', bg: '#FFFC00' },
  pinterest: { file: 'logos/pinterest.svg' },
  reddit: { file: 'logos/reddit.svg' },
  zoom: { file: 'logos/zoom.svg' },
  firebase: { file: 'logos/firebase.svg' },
  'google-bigquery': { file: 'logos/google-bigquery.svg' },
  facebook: { file: 'logos/facebook.svg' },
  // The X and ChatGPT Ads (OpenAI) conversion cards, 4 Nov 2026: the Simple
  // Icons paths, black, checked against the files (SOURCES.md).
  x: { file: 'logos/x.svg' },
  openai: { file: 'logos/openai.svg' },

  // The brands' own files (source URL for each in SOURCES.md).
  'google-ads': { file: 'logos/google-ads.svg' },
  'google-analytics': { file: 'logos/google-analytics.svg' },
  'google-tag-manager': { file: 'logos/google-tag-manager.svg' },
  'google-sheets': { file: 'logos/google-sheets.svg' },
  'google-calendar': { file: 'logos/google-calendar.svg' },
  // The organic sources a shopper arrives from (order journey, 11 Oct 2026):
  // Google's own "G" and YouTube icon, Instagram's own app icon (WebP: a PNG
  // of its gradient is over the size budget), Bing's own touch icon.
  'google-g': { file: 'logos/google-g.svg' },
  youtube: { file: 'logos/youtube.svg' },
  instagram: { file: 'logos/instagram.webp', fit: 'bleed' },
  bing: { file: 'logos/bing.png' },
  outlook: { file: 'logos/outlook.png' },
  'cal-com': { file: 'logos/cal-com.png', fit: 'bleed' },
  magento: { file: 'logos/magento.png' },
  shopflo: { file: 'logos/shopflo.png', fit: 'bleed' },
  gokwik: { file: 'logos/gokwik.png' },
  pragma: { file: 'logos/pragma.svg' },
  shiprocket: { file: 'logos/shiprocket.png', fit: 'bleed' },
  delhivery: { file: 'logos/delhivery.png', fit: 'bleed' },
  clickpost: { file: 'logos/clickpost.png', fit: 'bleed' },
  samcart: { file: 'logos/samcart.svg' },
  leadsquared: { file: 'logos/leadsquared.png' },
  klaviyo: { file: 'logos/klaviyo.png' },
  activecampaign: { file: 'logos/activecampaign.png', fit: 'bleed' },
  exotel: { file: 'logos/exotel.png' },
  twilio: { file: 'logos/twilio.svg' },
  knowlarity: { file: 'logos/knowlarity.png', bg: '#2D2776' },
  myoperator: { file: 'logos/myoperator.png', fit: 'bleed' },
  mcube: { file: 'logos/mcube.png' },
  ozonetel: { file: 'logos/ozonetel.png' },
  // servetel.in now redirects to acefone.com; this is the mark it shows there.
  servetel: { file: 'logos/servetel.png' },
  vapi: { file: 'logos/vapi.svg', fit: 'bleed' },
  bolna: { file: 'logos/bolna.png', fit: 'bleed' },
  linkedin: { file: 'logos/linkedin.png', fit: 'bleed' },
  'microsoft-ads': { file: 'logos/microsoft-ads.png' },
  appbrew: { file: 'logos/appbrew.svg', fit: 'bleed' },

  // Detected as a traffic source (catalogue LOOKALIKES).
  bitespeed: { file: 'logos/bitespeed.png', fit: 'bleed' },
  wati: { file: 'logos/wati.svg' },
  interakt: { file: 'logos/interakt.svg' },
  aisensy: { file: 'logos/aisensy.png' },
  tapcart: { file: 'logos/tapcart.svg' },
  // vajro.com now redirects to superfans.io; this is its app icon there.
  vajro: { file: 'logos/vajro.png', fit: 'bleed' },
  plobal: { file: 'logos/plobal.png' },
  shopney: { file: 'logos/shopney.png' },
  apptile: { file: 'logos/apptile.png', fit: 'bleed' },
  mobiloud: { file: 'logos/mobiloud.png', fit: 'bleed' },
  twinr: { file: 'logos/twinr.png', fit: 'bleed' },
  // swipecart.co now redirects to swipe.ai; this is its site icon there.
  swipecart: { file: 'logos/swipecart.png' },
};

/**
 * Card key -> logo, for every key whose logo is not simply its own name.
 * Covers the Integrations catalogue, the Destinations gallery (BRAND_OF) and
 * the older BrandMark keys the forwarding grids pass.
 */
export const LOGO_KEY = {
  // Integrations catalogue
  shiprocket_checkout: 'shiprocket',
  calcom: 'cal-com',
  google_calendar: 'google-calendar',
  microsoft_calendar: 'outlook',
  meta_lead_ads: 'meta',
  snap_lead_ads: 'snapchat',
  linkedin_lead_ads: 'linkedin',
  whatsapp_cloud: 'whatsapp',
  meta_catalog: 'meta',
  myoperator_ai: 'myoperator',
  meta_capi: 'meta',
  // Microsoft Ads conversions: the Microsoft mark the paid Bing traffic cards
  // already wear (owner, 4 Nov 2026: the card had no logo).
  microsoft: 'microsoft-ads',
  google_ads_dm: 'google-ads',
  google_ads_api: 'google-ads',
  ga4: 'google-analytics',
  sgtm: 'google-tag-manager',
  sgtm_enrichment: 'google-tag-manager',
  server_container: 'google-tag-manager',
  meta_audiences: 'meta',
  meta_spend: 'meta',
  google_spend: 'google-ads',
  spend_sheet: 'google-sheets',
  sheets_bridge: 'google-sheets',
  firestore: 'firebase',
  bigquery: 'google-bigquery',
  // Destinations gallery and the forwarding grids
  meta_ads: 'meta',
  audiences: 'meta',
  enrich: 'google-tag-manager',
  google_dm: 'google-ads',
  google_ads_g: 'google-ads',
  sheets: 'google-sheets',
  mobile_app: 'appbrew',
};

/**
 * Entries that are ours or a category, not a brand: they keep a lucide icon
 * tile, because a logo there would be a fake one. The catalogue test holds
 * every other key to a real file.
 */
export const GENERIC_KEYS = new Set([
  'shipping_generic', 'email_sending', 'otp_sms', 'calling_generic', 'ai_calling_generic', 'call_tracking',
  'partner_api', 'outbound_webhooks',
  // Destinations: CookieBees' own pieces
  'routing', 'catalog_ids', 'ecom_events',
  // Postscript and Appmaker (detected traffic sources, 11 Oct 2026): their
  // marks wait for the owner to pick from the official files. Same rule.
  'postscript', 'appmaker',
  // Zoho CRM: same as above, Zoho's mark is not in public/logos yet. Remove
  // from this list when the file (and its SOURCES.md row) lands.
  'zoho_crm',
  // Taboola and Outbrain conversions (12 Oct 2026): the owner chooses these
  // marks. Same rule: take each off this list when its file (and SOURCES.md
  // row) lands. (Microsoft Ads, X and ChatGPT Ads came off on 4 Nov 2026.)
  'taboola', 'outbrain',
  // MoEngage, WebEngage and CleverTap (engagement tools, 19 Oct 2026): their
  // marks wait for the owner to pick from the official files. Same rule.
  'moengage', 'webengage', 'clevertap', 'crm_custom']);

/** The logo for a card key, or null. */
export function logoFor(key) {
  if (!key) return null;
  return LOGOS[LOGO_KEY[key] || key] || null;
}

export const logoSrc = (logo) => (logo ? `${LOGO_BASE}${logo.file}` : null);
